import React, { memo, FC } from "react";
import { Alert, Typography } from "@mui/material";
import Choices from "../Choices";
import ReusableAccordian from "../ReusableAccordion";
import { Image } from "cloudinary-react";
import ScreenPocketAccordian from "../ScreenPocket/ScreenPocketAccordian";
import {
  screenLayoutImages,
  screenColors,
  singleScreenLayout,
  addScreen,
  addDoubleScreen,
  addPairSwing,
  layoutLeft,
  layoutRight,
} from "../../initialValues/screens";
import { RowLayout, ColLayout } from "../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import useScreenSelector from "./useScreenSelector";
import { useIsReadOnlyContextProviderContext } from "../../context/IsReadOnlyContext";
import PleaseNote from "../PleaseNote";

const ColLayoutLeftDivider = styled("div")(({ theme }) => ({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  justifyContent: "flex-start",
  display: "flex",
  flexDirection: "column",
  borderLeft: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
}));

const ColLayoutRight = styled("div")(({ theme }) => ({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  justifyContent: "flex-start",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(1, 2),
}));

const ColLayoutTopDivider = styled("div")(({ theme }) => ({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  borderTop: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
}));

const StyledSpan = styled("span")({
  color: "red",
  fontSize: 22,
});

interface ScreenProps {
  noSinglePocktRange: boolean;
  isPocketAllowed: boolean;
  widerThan16: boolean;
  widerThanLimitForPicoSwingSingleScreen: boolean;
}

interface AddScreenChoicesProps {
  addScreenOptions:
    | typeof addScreen
    | typeof addDoubleScreen
    | typeof addPairSwing;
  handleAddScreenClick: (label: string, isPairSwing?: boolean) => void;
  screenVal: string | undefined;
}

const AddScreenChoices: FC<AddScreenChoicesProps> = ({
  addScreenOptions,
  handleAddScreenClick,
  screenVal,
}) => {
  const { readOnly } = useIsReadOnlyContextProviderContext();
  return (
    <RowLayout>
      {addScreenOptions.map((state) => (
        <div
          key={state.id}
          onClick={() => (readOnly ? null : handleAddScreenClick(state.label))}
        >
          <Choices
            label={state.label}
            variant={state.label === screenVal ? "filled" : "outlined"}
            checkMark={state.label === screenVal}
            wide={true}
            disabled={readOnly}
          />
        </div>
      ))}
    </RowLayout>
  );
};

interface ScreenColorChoicesProps {
  handleScreenColorClick: (label: string) => void;
  screenColorChoice: string | undefined;
}

const ScreenColorChoices: FC<ScreenColorChoicesProps> = ({
  handleScreenColorClick,
  screenColorChoice,
}) => {
  const { readOnly } = useIsReadOnlyContextProviderContext();
  return (
    <RowLayout>
      {screenColors.map((state) => (
        <div
          key={state.id}
          onClick={() =>
            readOnly ? null : handleScreenColorClick(state.label)
          }
        >
          <Choices
            label={state.label}
            variant={state.label === screenColorChoice ? "filled" : "outlined"}
            checkMark={state.label === screenColorChoice}
            disabled={readOnly}
          />
        </div>
      ))}
    </RowLayout>
  );
};

const ScreenAccordian: FC<ScreenProps> = ({
  noSinglePocktRange,
  isPocketAllowed,
  widerThan16,
  widerThanLimitForPicoSwingSingleScreen,
}) => {
  const {
    handleScreenColorClick,
    handleLayoutClick,
    handleAddScreenClick,
    theme,
    isCornerUnit,
    isComboUnit,
    isDoubleScreenSelected,
    stiles,
    isSwingPair,
    doorType,
    screenVal,
    screenLayout,
    screenColorChoice,
    oneDirectionConfig,
    oneSideOddConfig,
    defaultPocketWide,
    screenImgShown,
    screenPocketImgShown,
    isScreenNotRequired,
    isPicoSwing,
  } = useScreenSelector(
    widerThan16,
    widerThanLimitForPicoSwingSingleScreen,
    noSinglePocktRange,
    isPocketAllowed
  );

  const isCornerOrCombo = isCornerUnit || isComboUnit;

  const showBothScreenOptions =
    (isSwingPair && !widerThanLimitForPicoSwingSingleScreen) ||
    ((doorType === "Bi-fold" || doorType === "Bi-fold Window") && !widerThan16);

  const showOnlyDoubleScreenOption =
    (isSwingPair && widerThanLimitForPicoSwingSingleScreen) ||
    (isCornerOrCombo && !widerThan16) ||
    (!isCornerOrCombo && widerThan16 && !isPicoSwing);

  const showOnlySingleScreenOption =
    !isCornerOrCombo &&
    ((!noSinglePocktRange && !widerThan16 && !isSwingPair) ||
      (noSinglePocktRange && !widerThan16));

  const singleScreenSelected = screenVal === "Add Screen";

  const { readOnly } = useIsReadOnlyContextProviderContext();

  return (
    <>
      <ReusableAccordian
        accordionName={isScreenNotRequired ? "screenNotRequired" : "screen"}
        heading="Brio Retractable Screen"
        secondaryHeading="Select Screen Option"
      >
        {isCornerOrCombo && widerThan16 ? (
          <Alert severity="error">
            Screens not available if width is greater than 192 inches
          </Alert>
        ) : (
          <ColLayout>
            <PleaseNote
              notes={[
                isDoubleScreenSelected &&
                oneSideOddConfig &&
                oneDirectionConfig &&
                isPocketAllowed ? (
                  "Pocketing Screen required to avoid blocking handle hardware"
                ) : isDoubleScreenSelected &&
                  !oneSideOddConfig &&
                  oneDirectionConfig &&
                  isPocketAllowed ? (
                  "Pocketing Screen recommended on primary side"
                ) : stiles?.includes("Slimline") && isPocketAllowed ? (
                  "Pocketing Screen recommended for Slimline"
                ) : stiles?.includes("Pico") && isPocketAllowed ? (
                  <>
                    Pocketing Screen recommended for{" "}
                    <img
                      alt="pico txt"
                      src={`https://res.cloudinary.com/ag-millworks/image/upload/Pico_Text/svg/pico_black.svg`}
                      style={{ width: 50, height: "auto", paddingLeft: 3 }}
                    />
                  </>
                ) : null,
                "Up-charge applies for all screens",
              ]}
            />

            {!screenVal ? (
              <AddScreenChoices
                addScreenOptions={
                  showBothScreenOptions
                    ? addPairSwing
                    : showOnlyDoubleScreenOption
                      ? addDoubleScreen
                      : showOnlySingleScreenOption
                        ? addScreen
                        : []
                }
                handleAddScreenClick={handleAddScreenClick}
                screenVal={screenVal}
              />
            ) : (
              <>
                {showBothScreenOptions ? (
                  <>
                    <AddScreenChoices
                      addScreenOptions={addPairSwing}
                      handleAddScreenClick={handleAddScreenClick}
                      screenVal={screenVal}
                    />

                    <RowLayout>
                      <ColLayoutRight>
                        {singleScreenSelected && (
                          <>
                            <Typography variant="subtitle1">
                              Screen Layout
                              <StyledSpan>*</StyledSpan>
                            </Typography>

                            <RowLayout>
                              {singleScreenLayout.map((state) => (
                                <div
                                  key={state.id}
                                  onClick={() =>
                                    readOnly
                                      ? null
                                      : handleLayoutClick(state.label)
                                  }
                                >
                                  <Choices
                                    label={state.label}
                                    variant={
                                      state.label === screenLayout ||
                                      state.pdfLabel === screenLayout
                                        ? "filled"
                                        : "outlined"
                                    }
                                    checkMark={
                                      state.label === screenLayout ||
                                      state.pdfLabel === screenLayout
                                    }
                                    disabled={readOnly}
                                  />
                                </div>
                              ))}
                            </RowLayout>
                          </>
                        )}
                      </ColLayoutRight>
                      <div
                        style={{
                          marginBottom: 10,
                          marginTop: 20,
                          alignItems: "center",
                          display: "flex",
                          borderLeft: singleScreenSelected
                            ? `2px solid ${theme.palette.divider}`
                            : "none",
                          flexDirection: singleScreenSelected
                            ? "column"
                            : "column",
                          padding: singleScreenSelected
                            ? theme.spacing(1, 2)
                            : 0,
                        }}
                      >
                        {screenVal === "Add Screen" && (
                          <Typography variant="subtitle1">
                            Screen Extrusion Color
                            <StyledSpan>*</StyledSpan>
                          </Typography>
                        )}

                        <ScreenColorChoices
                          handleScreenColorClick={handleScreenColorClick}
                          screenColorChoice={screenColorChoice}
                        />
                      </div>
                    </RowLayout>
                  </>
                ) : showOnlyDoubleScreenOption ? (
                  <RowLayout>
                    <ColLayout style={{ marginTop: 0 }}>
                      <AddScreenChoices
                        addScreenOptions={addDoubleScreen}
                        handleAddScreenClick={handleAddScreenClick}
                        screenVal={screenVal}
                      />
                      <ColLayoutTopDivider>
                        <Typography variant="subtitle1">
                          Screen Extrusion Color
                          <span style={{ color: "red", fontSize: 22 }}>*</span>
                        </Typography>

                        <ScreenColorChoices
                          handleScreenColorClick={handleScreenColorClick}
                          screenColorChoice={screenColorChoice}
                        />
                      </ColLayoutTopDivider>
                    </ColLayout>
                  </RowLayout>
                ) : showOnlySingleScreenOption ? (
                  <>
                    <AddScreenChoices
                      addScreenOptions={addScreen}
                      handleAddScreenClick={handleAddScreenClick}
                      screenVal={screenVal}
                    />

                    <RowLayout>
                      <ColLayoutRight>
                        <Typography variant="subtitle1">
                          Screen Layout
                          <StyledSpan>*</StyledSpan>
                        </Typography>

                        <RowLayout>
                          {!oneDirectionConfig
                            ? singleScreenLayout.map((state) => (
                                <div
                                  key={state.id}
                                  onClick={() =>
                                    readOnly
                                      ? null
                                      : handleLayoutClick(state.label)
                                  }
                                >
                                  <Choices
                                    label={state.label}
                                    variant={
                                      state.label === screenLayout ||
                                      state.pdfLabel === screenLayout
                                        ? "filled"
                                        : "outlined"
                                    }
                                    checkMark={
                                      state.label === screenLayout ||
                                      state.pdfLabel === screenLayout
                                    }
                                    disabled={readOnly}
                                  />
                                </div>
                              ))
                            : oneDirectionConfig[1] === "L"
                              ? layoutLeft.map((state) => (
                                  <div key={state.id}>
                                    <Choices
                                      label={state.label}
                                      variant="filled"
                                      checkMark={true}
                                      disabled={readOnly}
                                    />
                                  </div>
                                ))
                              : oneDirectionConfig[1] === "R"
                                ? layoutRight.map((state) => (
                                    <div key={state.id}>
                                      <Choices
                                        label={state.label}
                                        variant="filled"
                                        checkMark={true}
                                        disabled={readOnly}
                                      />
                                    </div>
                                  ))
                                : null}
                        </RowLayout>
                      </ColLayoutRight>
                      <ColLayoutLeftDivider>
                        <Typography variant="subtitle1">
                          Screen Extrusion Color
                          <StyledSpan>*</StyledSpan>
                        </Typography>

                        <ScreenColorChoices
                          handleScreenColorClick={handleScreenColorClick}
                          screenColorChoice={screenColorChoice}
                        />
                      </ColLayoutLeftDivider>
                    </RowLayout>
                  </>
                ) : null}

                {screenImgShown &&
                  screenLayoutImages
                    .filter((image) => image.label === screenImgShown)
                    .map((imageShown) => (
                      <RowLayout key={imageShown.label}>
                        <ColLayout
                          style={{
                            marginTop: 10,
                            marginBottom: 5,
                          }}
                        >
                          <Image
                            cloudName="ag-millworks"
                            secure="true"
                            publicId={imageShown.img}
                          />
                          <Typography
                            style={{ alignSelf: "center" }}
                            variant="caption"
                          >
                            *As viewed from exterior
                          </Typography>
                        </ColLayout>
                      </RowLayout>
                    ))}
              </>
            )}
          </ColLayout>
        )}
      </ReusableAccordian>

      {screenVal && isPocketAllowed && (
        <ScreenPocketAccordian
          isDoubleScreenSelected={isDoubleScreenSelected || false}
          oneSideOddConfig={oneSideOddConfig}
          defaultPocketWide={defaultPocketWide}
          displayedPocketImg={screenPocketImgShown}
          isPocketAllowed={isPocketAllowed}
        />
      )}
    </>
  );
};

export default memo(ScreenAccordian);
